import * as React from 'react';
import * as autoBind from 'react-autobind';
import {ControlButton, TscButtonType, AlertModal} from '@techsmith/tsc-cloud-style-guide';
import Strings from '../../globals/Strings';
import FeedbackUserService from '../../services/FeedbackUserService/FeedbackUserService';
import ReviewerGroupModel from '../../types/ReviewerGroupModel';
import Themeable from '../../types/Themeable';
import LoadingSpinner from '../LoadingSpinner/LoadingSpinner';

export default class ReviewerGroupsList extends React.Component<ReviewerGroupListProps, ReviewerGroupsListState> {
   readonly state: ReviewerGroupsListState = {showAlert: false, pendingGroupId: null, isSubmitting: false};

   constructor(props: ReviewerGroupListProps) {
      super(props);
      autoBind(this);
   }

   onCancelDeletion() {
      this.setState({showAlert: false, pendingGroupId: null});
   }

   onPerformDeletion() {
      this.setState({isSubmitting: true});

      return FeedbackUserService.deleteReviewerGroup(this.state.pendingGroupId).then(() => {
         this.setState({isSubmitting: false, showAlert: false, pendingGroupId: null});
      });
   }

   getReviewerGroupDiv(reviewerGroup: ReviewerGroupModel, index: number): React.ReactElement<any> {
      const {themeName, onEdit} = this.props;
      const strings = Strings.labels.groupsMenu;
      const memberCount = reviewerGroup.GroupMembers.length;
      const groupMemberCountText = (memberCount === 1 ? strings.oneMember : strings.members).replace('{param0}', reviewerGroup.GroupMembers.length + '');

      const onDelete = () => {
         this.setState({showAlert: true, pendingGroupId: reviewerGroup.Id});
      };

      const onEditClick = () => {
         onEdit(reviewerGroup);
      };

      return (
         <div className="row" key={index}>
            <div className="reviewer-column reviewer" key={index}>
               <div className="ellipsis" title={reviewerGroup.GroupName}>{reviewerGroup.GroupName}</div>
               <div className="group-member-count">{groupMemberCountText}</div>
            </div>
            <div className="row-buttons">
               <ControlButton themeName={themeName} label={Strings.labels.delete} buttonType={TscButtonType.secondary} isFullWidth={false} onClick={onDelete} title={Strings.labels.deleteGroup} testId="delete-group"/>
               <ControlButton themeName={themeName} label={Strings.labels.edit} buttonType={TscButtonType.secondary} isFullWidth={false} onClick={onEditClick} title={Strings.labels.editGroup} testId="edit-group"/>
            </div>
         </div>
      );
   }

   render(): React.ReactElement<any> {
      const {reviewerGroups, onClose, onCreate, themeName} = this.props;
      const {isSubmitting, showAlert} = this.state;
      const {emptyGroups, cancel, close, addNewGroup, deleteGroup, confirmGroupDeletionText} = Strings.labels;
      const alertContentClasses = isSubmitting ? 'alert-body' : 'alert';
      const alertContent =
         <div className={alertContentClasses}>
            {isSubmitting && <LoadingSpinner/>}
            {!isSubmitting && confirmGroupDeletionText}
         </div>;

      return (
         <div className="manage-group">
            <div className="group-table">
               {reviewerGroups.length === 0 && <div className="center-group-empty-state">{emptyGroups}</div>}
               {reviewerGroups.length > 0 && reviewerGroups.map((group, index) => this.getReviewerGroupDiv(group, index))}
            </div>
            <div className="button-group">
               <ControlButton
                  themeName={themeName}
                  buttonType={TscButtonType.secondary}
                  isFullWidth={true}
                  onClick={onClose}
                  label={close}
                  testId="cancel-button"
               />
               <ControlButton
                  themeName={themeName}
                  buttonType={TscButtonType.primary}
                  isFullWidth={true}
                  label={addNewGroup}
                  onClick={onCreate}
                  testId="create-group-button"/>
            </div>
            {showAlert && <AlertModal title={deleteGroup}
                                      onClose={this.onCancelDeletion}
                                      closeContent={cancel}
                                      onConfirm={this.onPerformDeletion}
                                      confirmContent={Strings.labels.delete}
                                      confirmButtonType={TscButtonType.destructive}
                                      onDismiss={this.onCancelDeletion}
                                      dismissContent={cancel}
                                      hideButtons={isSubmitting}
                                      testId="delete-group-alert">
               {alertContent}
            </AlertModal>}
         </div>
      );
   }
}

interface ReviewerGroupListProps extends Themeable {
   reviewerGroups: ReviewerGroupModel[];
   onClose(): void;
   onCreate(): void;
   onEdit(reviewerGroupModel: ReviewerGroupModel): void;
}

interface ReviewerGroupsListState {
   showAlert: boolean;
   pendingGroupId: string;
   isSubmitting: boolean;
}
